/**
 * Firebase Configuration
 *
 * Initializes Firebase app with environment variables.
 * Uses lazy initialization to avoid errors when Firebase is disabled.
 */

import { initializeApp, getApps, FirebaseApp } from 'firebase/app'
import { connectAuthEmulator, getAuth, Auth } from 'firebase/auth'
import { getFirestore, Firestore } from 'firebase/firestore'

type RuntimeImportMeta = ImportMeta & {
  env?: Record<string, string | undefined>
}

const runtimeEnv = (import.meta as RuntimeImportMeta).env
const firebaseConfig = {
  apiKey: runtimeEnv?.VITE_FIREBASE_API_KEY || '',
  authDomain: runtimeEnv?.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: runtimeEnv?.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: runtimeEnv?.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: runtimeEnv?.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: runtimeEnv?.VITE_FIREBASE_APP_ID || '',
}
const authEmulatorHost = runtimeEnv?.VITE_FIREBASE_AUTH_EMULATOR_HOST || ''

// Check if Firebase is configured
export const isFirebaseConfigured = (): boolean => {
  return !!(firebaseConfig.apiKey && firebaseConfig.projectId)
}

// Lazy-initialize Firebase app
let app: FirebaseApp | null = null
let auth: Auth | null = null
let db: Firestore | null = null
let authEmulatorConnected = false

export const getFirebaseApp = (): FirebaseApp | null => {
  if (!isFirebaseConfigured()) {
    return null
  }
  if (!app) {
    app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0]
  }
  return app
}

export const getFirebaseAuth = (): Auth | null => {
  const firebaseApp = getFirebaseApp()
  if (!firebaseApp) {
    return null
  }
  if (!auth) {
    auth = getAuth(firebaseApp)
    if (authEmulatorHost && !authEmulatorConnected) {
      const emulatorUrl = authEmulatorHost.startsWith('http')
        ? authEmulatorHost
        : `http://${authEmulatorHost}`
      connectAuthEmulator(auth, emulatorUrl, { disableWarnings: true })
      authEmulatorConnected = true
    }
  }
  return auth
}

export const getFirestoreDb = (): Firestore | null => {
  const firebaseApp = getFirebaseApp()
  if (!firebaseApp) {
    return null
  }
  if (!db) {
    db = getFirestore(firebaseApp)
  }
  return db
}

export { firebaseConfig }
